// @flow
import React from 'react';
import { components } from '@peachjar/components';
import UploadStatusBox from '../components/UploadStatusBox';
import StaffListTable from '../components/StaffListTable';
import DownloadButtonsSection from '../components/DownloadButtonSection';
import { styles } from './UploadStaffTab.css';
import UploadCSVContainer from '../../parent/components/UploadCSVContainer';
import ReadOnly from '../../_shared/ReadOnly';

const { Buttons, NumberedRow } = components;
const { ButtonPrimaryLarge, ButtonSecondaryLarge } = Buttons;

type CSVError = {
  errorMessageHeader: string,
  errors: Array<string>,
};

type StaffList = {
  name: string,
  nces_id: string,
  site_name: string,
  first_name: string,
  last_name: string,
  email: string,
  user_type: string,
  admin_role: string,
  approver_role: string,
  uploader_role: string,
  subscribe: string,
};

type ReduxDispatchProps = {|
  +handleFileUpload: (event: any) => void,
  +handleInstructionsDownload: () => void,
  +handleCsvTemplateDownload: () => void,
  +resetFileUpload: () => void,
  +setShowTable: () => void,
  +hideTable: () => void,
  +setUploadingPercentage: (value: number) => void,
  +sendCSVContents: () => void,
  +setStaffTabView: number => void,
|};

type ReduxStateProps = {|
  +showTable: boolean,
  +instructionsViewed: boolean,
  +templateDownloaded: boolean,
  +uploading: boolean,
  +uploadComplete: boolean,
  +uploadPercentage: number,
  +filename: string,
  +order: string,
  +orderBy: string,
  +data: Array<StaffList>,
  +errors: Array<CSVError>,
  +haveStaffBeenLoaded: boolean,
|};

type Props = {|
  ...ReduxDispatchProps,
  ...ReduxStateProps,
  +ncesSchoolId: string,
|};

class UploadStaff extends React.Component<Props> {
  enableButton = () => {
    const {
      instructionsViewed,
      templateDownloaded,
      uploading,
      uploadComplete,
    } = this.props;

    if (
      !instructionsViewed ||
      !templateDownloaded ||
      (uploading && !uploadComplete)
    )
      return false;

    return true;
  };

  handleFileUpload = (fileList: SyntheticInputEvent<HTMLInputElement>) => {
    if (fileList.target.files.length > 0) {
      const file: File = fileList.target.files[0];

      this.props.handleFileUpload(file);
      // Clearing target value by reassignment
      // eslint-disable-next-line
      fileList.target.value = '';
    }
  };

  handleRequestSort = (
    event: SyntheticEvent<HTMLButtonElement>,
    property: string
  ) => {
    const orderBy = property;
    let order = 'desc';

    if (this.props.orderBy === property && this.props.order === 'desc') {
      order = 'asc';
    }
  };

  renderUploadButton = () => {
    if (this.props.uploadComplete) {
      return (
        <ButtonSecondaryLarge
          data-testid="click-uploadcsv"
          className={styles.button}
        >
          Upload CSV File
        </ButtonSecondaryLarge>
      );
    } else if (this.props.uploading) {
      return (
        <ButtonPrimaryLarge
          data-testid="click-uploadcsv"
          className={styles.button}
          disabled
        >
          Upload CSV File
        </ButtonPrimaryLarge>
      );
    } else if (
      !this.props.instructionsViewed ||
      !this.props.templateDownloaded
    ) {
      return (
        <ButtonPrimaryLarge
          data-testid="click-uploadcsv"
          className={styles.button}
          disabled
        >
          Upload CSV File
        </ButtonPrimaryLarge>
      );
    }

    return (
      <ButtonPrimaryLarge data-testid="click-uploadcsv">
        Upload CSV File
      </ButtonPrimaryLarge>
    );
  };

  renderContinueButton = () => {
    if (
      this.props.uploadComplete &&
      this.props.uploadPercentage === 100 &&
      this.props.errors.length === 0
    ) {
      return (
        <ButtonPrimaryLarge
          className={(styles.continueButton, styles.button)}
          onClick={this.props.setShowTable}
          data-testid="click-continue"
        >
          Continue
        </ButtonPrimaryLarge>
      );
    }

    return (
      <ButtonPrimaryLarge
        data-testid="click-continue"
        className={styles.continueButton}
        disabled
      >
        Continue
      </ButtonPrimaryLarge>
    );
  };

  render() {
    const {
      showTable,
      instructionsViewed,
      uploading,
      uploadComplete,
      uploadPercentage,
      filename,
      order,
      orderBy,
      ncesSchoolId,
      data,
      errors,
      sendCSVContents,
      handleInstructionsDownload,
      handleCsvTemplateDownload,
      hideTable,
      haveStaffBeenLoaded,
      setStaffTabView,
    } = this.props;

    const READ_ONLY_MESSAGE =
      'Your staff list has already been uploaded. You will be able to make updates once the setup process has been completed.';

    return (
      <div className={styles.container}>
        {haveStaffBeenLoaded ? (
          <div className={styles.readOnlyWrapper}>
            <ReadOnly message={READ_ONLY_MESSAGE} />
            <div className={styles.readOnlyContinueButtonWrapper}>
              <ButtonPrimaryLarge
                className={styles.continueButton}
                onClick={() => setStaffTabView(2)}
                data-testid="readonly-stafflist"
              >
                Continue
              </ButtonPrimaryLarge>
            </div>
          </div>
        ) : showTable ? (
          <div className={styles.tableWrapper}>
            <StaffListTable
              data={data}
              order={order}
              orderBy={orderBy}
              handleRequestSort={this.handleRequestSort}
            />
            <div className={styles.tableContinueButtonWrapper}>
              <div
                className={styles.tableContinueBackButton}
                onClick={hideTable}
              >
                <span>Reupload CSV File</span>
              </div>
              <ButtonPrimaryLarge
                className={styles.continueButton}
                onClick={sendCSVContents}
                data-testid="submit-stafflist"
              >
                Continue
              </ButtonPrimaryLarge>
            </div>
          </div>
        ) : (
          <div className={styles.uploadWrapper}>
            <NumberedRow rowNumber={1} headerText="Prepare CSV File">
              <DownloadButtonsSection
                handleInstructionsDownload={handleInstructionsDownload}
                handleCsvTemplateDownload={handleCsvTemplateDownload}
                showCsvTemplateButton={
                  instructionsViewed
                  // haveParentsBeenLoaded || instructionsViewed
                }
                ncesSchoolId={ncesSchoolId}
              />
            </NumberedRow>
            <NumberedRow rowNumber={2} headerText="Upload CSV File">
              {uploading && (
                <UploadStatusBox
                  uploadPercentage={uploadPercentage}
                  uploadComplete={uploadComplete}
                  filename={filename}
                  errors={errors}
                />
              )}
              <UploadCSVContainer
                showUploadMessage={uploading}
                enableUploadButton={this.enableButton()}
                onChangeHandler={this.handleFileUpload}
              />
              <div
                className="justify-content-end"
                style={{
                  display: 'flex',
                  width: '920px',
                  marginTop: '24px',
                }}
              >
                {this.renderContinueButton()}
              </div>
            </NumberedRow>
          </div>
        )}
      </div>
    );
  }
}

export default UploadStaff;



// WEBPACK FOOTER //
// ./src/Onboarding/Setup/staff/containers/UploadStaffTab.jsx